import { useCallback, useMemo } from 'react';
import type { EnvironmentAware } from '@gonfalon/app-context/types';
import { enableAiConfigModeTabs } from '@gonfalon/dogfood-flags/dogfood-flags';
import { createSearchParamsHook } from '@gonfalon/router/createSearchParamsHook';

import { getPaginatedCollectionOffsets } from '#hooks/usePaginatedCollection';
import { PaginationClick } from '#utils/paginationUtils';

import type {
  AiConfigFilter,
  AiConfigFilterSubset,
  FilterExcludeOptions,
  SortOrdering,
  UIListViewFilter,
} from '../helpers';
import {
  clearProjectFilters as clearProjectFilters_,
  hasActiveFilters,
  parseEnvironmentAwareUISortParamsFromBrowser as parseEnvironmentAwareUIAiConfigListViewFromBrowser,
  parseUIAiConfigFilterFromBrowser,
  serializeEnvironmentAwareUISortParamsForBrowser as serializeEnvironmentAwareUIAiConfigListViewForBrowser,
  serializeUIAiConfigFilterForBrowser,
} from '../helpers';

import { useCachedAiConfigListFilters } from './useCachedAiConfigListFilters';
import { useCachedAiConfigSortOrdering } from './useCachedAiConfigSortOrdering';

const useAiConfigListFiltersSearchParams = createSearchParamsHook<AiConfigFilter>({
  parse: parseUIAiConfigFilterFromBrowser,
  serialize: serializeUIAiConfigFilterForBrowser,
});

const useAiConfigListSearchParams = createSearchParamsHook({
  parse: parseEnvironmentAwareUIAiConfigListViewFromBrowser,
  serialize: serializeEnvironmentAwareUIAiConfigListViewForBrowser,
});

export function useAiConfigFilters() {
  const sortOrderCache = useCachedAiConfigSortOrdering();
  const [aiConfigListParams, setAiConfigListParams, createSearchParams] = useAiConfigListSearchParams();
  const filterCache = useCachedAiConfigListFilters();
  const [projectFilters] = useAiConfigListFiltersSearchParams();

  // When mode tabs are enabled, mode is controlled by tabs and should not be counted/cleared as a filter
  const isModeTabsEnabled = enableAiConfigModeTabs();
  const filterExcludeOptions: FilterExcludeOptions = useMemo(
    () => (isModeTabsEnabled ? { excludeKeys: ['mode'] } : {}),
    [isModeTabsEnabled],
  );

  const _updateFilters = (view: EnvironmentAware<UIListViewFilter>) => {
    filterCache.put(view.filter);
    sortOrderCache.put(view.sort);
    setAiConfigListParams({ ...aiConfigListParams, ...view });
  };

  const _updateFiltersAndResetPagination = (view: EnvironmentAware<UIListViewFilter>) =>
    _updateFilters({ ...aiConfigListParams, ...view, offset: 0 });

  const updateTextSearch = (query: string | undefined) =>
    _updateFiltersAndResetPagination({ ...aiConfigListParams, filter: { ...aiConfigListParams.filter, query } });

  const clearTextSearch = () => updateTextSearch(undefined);

  const updateOrdering = (sort: SortOrdering) => _updateFiltersAndResetPagination({ sort });

  const updateProjectFilters = (nextFilter: AiConfigFilterSubset) =>
    _updateFiltersAndResetPagination({ filter: { ...aiConfigListParams.filter, ...nextFilter } });

  const createProjectFiltersSearchParams = (nextFilter: AiConfigFilterSubset) =>
    createSearchParams({ filter: { ...aiConfigListParams.filter, ...nextFilter } });

  const clearProjectFilters = () =>
    _updateFiltersAndResetPagination({
      filter: {
        ...aiConfigListParams.filter,
        ...clearProjectFilters_(aiConfigListParams.filter ?? {}, filterExcludeOptions),
      },
    });

  const clearFiltersKeepView = () => {
    const currentView = aiConfigListParams.filter?.view;
    const currentMode = aiConfigListParams.filter?.mode;
    _updateFiltersAndResetPagination({
      filter: {
        ...(currentView ? { view: currentView } : {}),
        ...(currentMode ? { mode: currentMode } : {}),
      },
    });
  };

  const clearAllFilters = () =>
    _updateFiltersAndResetPagination({
      filter: {},
    });

  const updatePaginationOffset = useCallback(
    (direction: PaginationClick, totalCount: number) => {
      const offset = aiConfigListParams.offset;
      const limit = aiConfigListParams.limit;

      const { previousPageOffset, nextPageOffset, lastPageOffset } = getPaginatedCollectionOffsets({
        totalCount,
        offset: offset ? Number(offset) : undefined,
        limit: limit ? Number(limit) : undefined,
      });

      let nextOffset = offset ?? 0;

      switch (direction) {
        case PaginationClick.PREV:
          nextOffset = previousPageOffset;
          break;
        case PaginationClick.NEXT:
          nextOffset = nextPageOffset;
          break;
        case PaginationClick.LAST:
          nextOffset = lastPageOffset;
          break;
        case PaginationClick.FIRST:
        default:
          nextOffset = 0;
          break;
      }

      setAiConfigListParams({ ...aiConfigListParams, offset: nextOffset });
    },
    [aiConfigListParams, setAiConfigListParams],
  );

  const hasActive = hasActiveFilters(aiConfigListParams.filter, filterExcludeOptions);

  return {
    view: aiConfigListParams,
    hasActiveFilters: hasActive,
    filterExcludeOptions,
    projectFilters,
    updateProjectFilters,
    createProjectFiltersSearchParams,
    clearProjectFilters,
    clearFiltersKeepView,
    updateTextSearch,
    clearTextSearch,
    updateOrdering,
    updatePaginationOffset,
    clearAllFilters,
  } as const;
}
